import {
  Autocomplete,
  Checkbox,
  Grid,
  InputLabel,
  List,
  ListItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
  Box,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import InputFields from 'routes/Pages/components/InputFields/InputFields';
import { checklistCompletedOption, speciesOptions, yesNoOption } from '../Data/HFDropDowdData';
import { harvestingMaintanceVehicalCheckListoptionList } from '../Data/HfInputFieldsData';
import SectionAccordians from 'routes/Pages/components/Accordians/SectionAccordians';
import DetailedM3Table, { calcTotals, isTonnesUom } from './DetailedM3Table';

import { DropzoneUpload } from './InputsRendered';
import { EmployeeNameField } from 'routes/Pages/GenTask/components/SharedFormFields';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';

const dropDownStyles = {
  paddingBottom: '10px',
  fontSize: '0.825rem',
  color: 'black',
};

const autoCompleteSlotProps = {
  paper: {
    sx: {
      '& .MuiAutocomplete-option': {
        fontSize: '0.82rem',
        fontWeight: 500,
      },
    },
  },
};

const HFDailyHarvestingCloseForm = ({
  currDailyHarvestingData,
  setCurrDailyHarvestingData,
  spListObj,
  currentharvestingMaintenanceCheckListData,
  setCurrentharvestingMaintenanceCheckListData,
  productionData,
  setProductionData,
  files,
  isLoading,
  handleFileChange,
  handleDeleteFiles,
  viewFile,
  machineList,
}) => {

  const uom = currDailyHarvestingData['unitOfMeasure'] || 'M3';
  const isTonnes = isTonnesUom(uom);

  const { totalValue, totalM3 } = React.useMemo(
    () => calcTotals(productionData, uom),
    [productionData, uom]
  );

  useEffect(() => {
    setCurrDailyHarvestingData(prev => ({
      ...prev,
      totalValue: isTonnes && totalValue ? Number(totalValue.toFixed(2)) : totalM3,
      total_M3: totalM3 ? Number(totalM3.toFixed(2)) : '',
    }));
  }, [totalValue, totalM3, isTonnes, setCurrDailyHarvestingData]);

  const harvesterMachineOptions = React.useMemo(() => {
    return machineList.find(group => group.keyName === 'HarvesterMachine')?.values || [];
  }, [machineList]);

  const handleChangeDailyHarvestingData = (name, value) => {
    let updatedData = {
      [name]: value,
    };
    if (name === 'overnightStay' && value === 'No') {
      updatedData['fullNameAccommodation'] = '';
    }
    setCurrDailyHarvestingData(prevData => ({
      ...prevData,
      ...updatedData,
    }));
  };

  const handleHarvestingMachineChecklistChange = (name, value) => {
    setCurrentharvestingMaintenanceCheckListData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const [fixedProducts, setFixedProducts] = useState([]);

  // Fetch products whenever Forest / SP Number changes
  useEffect(() => {
    const forestSPName = currDailyHarvestingData['forestSPNumber'];
    const contractUniqueID = spListObj?.[forestSPName]?.contractUniqueID;

    if (!contractUniqueID) {
      setFixedProducts([]);
      return;
    }

    const fetchProducts = async () => {
      try {
        const res = await customAxios.get(`/Ops/GetContractProductDestinations/${contractUniqueID}`, {
          headers: authHeader(),
        });

        const productList = Array.from(
          new Set((res?.data || []).filter(item => item.productGroup).map(item => item.productGroup.trim())),
        );

        setFixedProducts(productList);
      } catch (err) {
        console.log('Error fetching contract products:', err);
        setFixedProducts([]);
      }
    };

    fetchProducts();
  }, [currDailyHarvestingData['forestSPNumber'], spListObj]);

  // Merge fetched products into productionData as default rows
  useEffect(() => {
    if (!fixedProducts || fixedProducts.length === 0) return;

    setProductionData(prevData => {
      const existingNames = new Set(prevData.map(row => row.productName));
      const newRows = fixedProducts
        .filter(name => !existingNames.has(name))
        .map((name, idx) => ({
          productNo: `${prevData.length + idx + 1}`,
          productName: name,
          m3: '',
          notes: '',
        }));

      if (newRows.length === 0) return prevData;
      return [...prevData, ...newRows];
    });
  }, [fixedProducts]);

  return (
    <Grid
      container
      spacing={1}
      sx={{
        '& .MuiTableCell-root': {
          fontSize: '0.825rem',
        },
        '& .MuiInputBase-root': {
          fontSize: '0.825rem !important',
        },
        paddingBottom: '1rem',
      }}>
      <EmployeeNameField
        value={currDailyHarvestingData['employeeName']}
        onChange={handleChangeDailyHarvestingData}
        isOperator
      />

      <InputFields
        label="Date & Time"
        helperText={'Enter Clock in Date & Time'}
        name="date_Time"
        required={true}
        value={currDailyHarvestingData['date_Time']}
        textFieldType={'datetime-local'}
        width={4}
        handleIpChange={handleChangeDailyHarvestingData}
      />

      <Grid item xs={6} sm={6} md={4}>
        <InputLabel size="small" sx={dropDownStyles} required>
          Was their tops left to one side for the forwarder?
        </InputLabel>
        <Autocomplete
          size="small"
          disablePortal
          name={'wasTheirTopsLeftToOneSideForTheForwarder'}
          value={currDailyHarvestingData['wasTheirTopsLeftToOneSideForTheForwarder'] || null}
          options={yesNoOption}
          onChange={(event, newValue) =>
            handleChangeDailyHarvestingData('wasTheirTopsLeftToOneSideForTheForwarder', newValue)
          }
          sx={{ fontSize: '0.8rem' }}
          renderInput={params => <TextField required {...params} size="small" fullWidth />}
          fullWidth
          slotProps={{ ...autoCompleteSlotProps }}
        />
      </Grid>

      {currDailyHarvestingData['wasTheirTopsLeftToOneSideForTheForwarder'] === 'No' && (
        <InputFields
          label="State Why?"
          name="wasTheirTopsLeftToOneSideForTheForwarderReason"
          required={currDailyHarvestingData['wasTheirTopsLeftToOneSideForTheForwarder'] === 'No'}
          value={currDailyHarvestingData['wasTheirTopsLeftToOneSideForTheForwarderReason']}
          textFieldType={'text'}
          multiline={true}
          width={12}
          handleIpChange={handleChangeDailyHarvestingData}
        />
      )}

      <Grid item xs={6} sm={6} md={4}>
        <InputLabel size="small" sx={dropDownStyles} required>
          Machine
        </InputLabel>
        <Autocomplete
          size="small"
          disablePortal
          name={'machine'}
          value={currDailyHarvestingData['machine'] || null}
          options={harvesterMachineOptions}
          onChange={(event, newValue) => handleChangeDailyHarvestingData('machine', newValue)}
          sx={{ fontSize: '0.8rem' }}
          renderInput={params => <TextField required {...params} size="small" fullWidth />}
          fullWidth
          slotProps={{ ...autoCompleteSlotProps }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} sx={{ alignContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: 'row-reverse' }}>
          <Typography variant="subtitle2">
            Subsistence Tracking(Tick Box if you were away from base for less than 10 hours )
          </Typography>
          <Checkbox
            checked={currDailyHarvestingData['subsistenceTracking'] || false}
            onChange={e => handleChangeDailyHarvestingData('subsistenceTracking', e.target.checked)}
          />
        </div>
      </Grid>

      <InputFields
        label="Non Productive Hours"
        name="nonProductiveHours"
        value={currDailyHarvestingData['nonProductiveHours']}
        textFieldType={'number'}
        width={4}
        handleIpChange={handleChangeDailyHarvestingData}
      />

      <InputFields
        label="Reason For Non Productive Hours"
        helperText={'This must be preapproved with your manager'}
        name="reasonForNonProductiveHours"
        value={currDailyHarvestingData['reasonForNonProductiveHours']}
        textFieldType={'text'}
        width={4}
        handleIpChange={handleChangeDailyHarvestingData}
        multiline={true}
      />

      <InputFields
        label="Manager Approved Downtime"
        name="managerApprovedDowntime"
        value={currDailyHarvestingData['managerApprovedDowntime']}
        textFieldType={'text'}
        width={4}
        handleIpChange={handleChangeDailyHarvestingData}
      />

      <Grid item xs={6} sm={6} md={4}>
        <InputLabel size="small" sx={dropDownStyles}>
          Site Bonus %
        </InputLabel>
        <Autocomplete
          size="small"
          disablePortal
          name={'siteBonus'}
          value={currDailyHarvestingData['siteBonus'] ?? null}
          options={[0, 25, 50, 75, 100]}
          getOptionLabel={option => `${option}`}
          onChange={(event, newValue) => handleChangeDailyHarvestingData('siteBonus', newValue)}
          sx={{ fontSize: '0.8rem' }}
          renderInput={params => <TextField {...params} size="small" fullWidth />}
          fullWidth
          slotProps={{ ...autoCompleteSlotProps }}
        />
      </Grid>

      <Grid item xs={6} sm={6} md={4}>
        <InputLabel size="small" sx={dropDownStyles}>
          Machine Bonus %
        </InputLabel>
        <Autocomplete
          size="small"
          disablePortal
          name={'machineBonus'}
          value={currDailyHarvestingData['machineBonus'] ?? null}
          options={[0, 25, 50, 75, 100]}
          getOptionLabel={option => `${option}`}
          onChange={(event, newValue) => handleChangeDailyHarvestingData('machineBonus', newValue)}
          sx={{ fontSize: '0.8rem' }}
          renderInput={params => <TextField {...params} size="small" fullWidth />}
          fullWidth
          slotProps={{ ...autoCompleteSlotProps }}
        />
      </Grid>

      <InputFields
        label="Manager Feedback"
        name="managerFeedback"
        value={currDailyHarvestingData['managerFeedback']}
        textFieldType={'text'}
        width={8}
        handleIpChange={handleChangeDailyHarvestingData}
        multiline={true}
      />

      <Grid item xs={12} sm={12} md={4} sx={{ alignContent: 'end' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: 'row-reverse' }}>
          <Typography variant="subtitle2">
            Moved location for signal ?(Tick if you needed to move location to get cellular signal)
          </Typography>
          <Checkbox
            checked={currDailyHarvestingData['movedLocationForSignal'] || false}
            onChange={e => handleChangeDailyHarvestingData('movedLocationForSignal', e.target.checked)}
          />
        </div>
      </Grid>

      <Grid item xs={6} sm={6} md={4}>
        <InputLabel size="small" sx={dropDownStyles} required>
          Forest or SP Number
        </InputLabel>
        <Autocomplete
          size="small"
          disablePortal
          name={'forestSPNumber'}
          value={currDailyHarvestingData['forestSPNumber'] || null}
          options={spListObj ? Object.keys(spListObj) : []}
          onChange={(event, newValue) => handleChangeDailyHarvestingData('forestSPNumber', newValue)}
          sx={{ fontSize: '0.8rem' }}
          renderInput={params => <TextField required {...params} size="small" fullWidth />}
          fullWidth
          slotProps={{ ...autoCompleteSlotProps }}
        />
      </Grid>

      <Grid item xs={6} sm={6} md={4}>
        <InputLabel size="small" sx={dropDownStyles} required>
          Species
        </InputLabel>
        <Autocomplete
          size="small"
          disablePortal
          name={'species'}
          value={currDailyHarvestingData['species'] || null}
          options={speciesOptions}
          onChange={(event, newValue) => handleChangeDailyHarvestingData('species', newValue)}
          sx={{ fontSize: '0.8rem' }}
          renderInput={params => <TextField required {...params} size="small" fullWidth />}
          fullWidth
          slotProps={{ ...autoCompleteSlotProps }}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12}>
        <SectionAccordians header={'Daily Maintenance Check List Instructions'}>
          <Typography variant="subtitle1">
            Daily Maintenance Every 10 Hours for Harvester/Forwarder Engine Separate Engine Owners Manual
          </Typography>
          <List>
            <ListItem>1.Check oil and liquid leaks repair if necessary</ListItem>
            <ListItem>2.Check any intake and exhaust air leaks</ListItem>
            <ListItem>3.Check the engine or level</ListItem>
          </List>
          <Typography variant="subtitle1">Bogies</Typography>
          <List>
            <ListItem>1.Check any leaks</ListItem>
          </List>
          <Typography variant="subtitle1">Hydraulic System</Typography>
          <List>
            <ListItem>1.Check any leaks</ListItem>
          </List>
          <Typography variant="subtitle1">Greasing</Typography>
          <List>
            <ListItem>1.Check any faultless operation of the lubrication system</ListItem>
            <ListItem>2.Grease grab/crane and centre section every 10 hours</ListItem>
          </List>
        </SectionAccordians>
      </Grid>

      <Grid item xs={12} sm={12} md={12}>
        <TableContainer component={Paper} sx={{ borderRadius: '10px', overflow: 'hidden', boxShadow: 1 }}>
          <Table border={1} size="small" sx={{ border: '1px solid #ccc', fontSize: '0.825rem' }}>
            <TableHead>
              <TableRow>
                <TableCell width="70%"></TableCell>
                <TableCell>Choose Completed or No Completed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {harvestingMaintanceVehicalCheckListoptionList.map((item, index) => (
                <TableRow key={`${index}_${item.label}`}>
                  <TableCell>{item.label}</TableCell>
                  <TableCell>
                    <Autocomplete
                      size="small"
                      disablePortal
                      name={item.key}
                      value={currentharvestingMaintenanceCheckListData[item.key] || null}
                      options={checklistCompletedOption}
                      onChange={(event, newValue) => handleHarvestingMachineChecklistChange(item.key, newValue)}
                      sx={{ fontSize: '0.8rem' }}
                      renderInput={params => <TextField {...params} size="small" fullWidth />}
                      fullWidth
                      slotProps={{ ...autoCompleteSlotProps }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {/* Detailed Table for M3 / Tonnes */}
      <Grid item xs={12} sm={12} md={12}>
        <DetailedM3Table
          productionData={productionData}
          setProductionData={setProductionData}
          currDailyHarvestingData={currDailyHarvestingData}
          handleChangeDailyHarvestingData={handleChangeDailyHarvestingData}
        />
      </Grid>

      {isTonnes && (
        <InputFields
          label="Total Value"
          name="totalValue"
          value={totalValue ? Number(totalValue.toFixed(2)) : ''}
          textFieldType="number"
          width={4}
          disabled
          handleIpChange={() => { }}
        />
      )}

      <InputFields
        label="Total M3"
        name="total_M3"
        value={totalM3 ? Number(totalM3.toFixed(2)) : ''}
        textFieldType="number"
        width={4}
        disabled
        handleIpChange={() => { }}
      />

      <InputFields
        label="Total M3 /Hr"
        name="total_M3PerHr"
        required={true}
        value={currDailyHarvestingData['total_M3PerHr'] ?? ''}
        textFieldType={'number'}
        multiline={false}
        width={4}
        disabled={false}
        handleIpChange={handleChangeDailyHarvestingData}
      />

      <Grid item xs={12} sm={12} md={6}>
        <DropzoneUpload label="File Upload" onFileSelect={handleFileChange('files')} multiple={true} />
        <Box
          sx={{
            display: 'flex',
            overflowX: 'auto',
            gap: 1,
            py: 1,
            px: 0.5,
            alignItems: 'center',
            minHeight: 60,
          }}>
          {isLoading ? (
            <Box sx={{ display: 'flex' }}>
              <CircularProgress size={22} />
              <Typography style={{ marginLeft: 8 }}>Fetching Docs ...</Typography>
            </Box>
          ) : files && files?.length > 0 ? (
            files.map((file, index) => (
              <Tooltip title={file.docName || file.documentName} key={index}>
                <div>
                  <Chip
                    label={
                      file?.documentName?.length > 20 ? file.documentName.slice(0, 18) + '…' : file.documentName
                    }
                    color="success"
                    sx={{ fontSize: '0.7rem', height: '24px', px: 1 }}
                    onDelete={() => handleDeleteFiles(file)}
                    onClick={() => viewFile(file)}
                  />
                </div>
              </Tooltip>
            ))
          ) : (
            <Typography>No files uploaded.</Typography>
          )}
        </Box>
      </Grid>

      <Grid item xs={6} sm={6} md={4}>
        <InputLabel size="small" sx={dropDownStyles} required>
          Overnight Stay
        </InputLabel>
        <Autocomplete
          size="small"
          disablePortal
          name={'overnightStay'}
          value={currDailyHarvestingData['overnightStay'] || null}
          options={yesNoOption}
          onChange={(event, newValue) => handleChangeDailyHarvestingData('overnightStay', newValue)}
          sx={{ fontSize: '0.8rem' }}
          renderInput={params => <TextField required {...params} size="small" fullWidth />}
          fullWidth
          slotProps={{ ...autoCompleteSlotProps }}
        />
      </Grid>

      {currDailyHarvestingData['overnightStay'] === 'Yes' && (
        <InputFields
          label="Full Name (Accommodation)"
          name="fullNameAccommodation"
          required={currDailyHarvestingData['overnightStay'] === 'Yes'}
          value={currDailyHarvestingData['fullNameAccommodation']}
          textFieldType={'text'}
          multiline={true}
          width={8}
          handleIpChange={handleChangeDailyHarvestingData}
        />
      )}

      <InputFields
        label="Additional Information"
        name="additionalInformation"
        required={false}
        value={currDailyHarvestingData['additionalInformation']}
        textFieldType={'text'}
        multiline={true}
        width={12}
        handleIpChange={handleChangeDailyHarvestingData}
      />
    </Grid>
  );
};

export default HFDailyHarvestingCloseForm;